"use client";

import React, { createContext, useContext } from "react";
import type { ComposeFormStateStableContextValue } from "@/lib/core/crm/types";

/**
 * The STABLE half of the compose form state. The per-keystroke watched subject
 * and body live in `ComposeWatchedFieldsContext` instead, so a consumer that
 * only reads this context is not re-rendered by typing.
 */
const ComposeFormStateContext = createContext<ComposeFormStateStableContextValue | null>(null);

interface ComposeFormStateProviderProps {
  children: React.ReactNode;
  value: ComposeFormStateStableContextValue;
}

export function ComposeFormStateProvider({
  children,
  value,
}: ComposeFormStateProviderProps) {
  return (
    <ComposeFormStateContext.Provider value={value}>
      {children}
    </ComposeFormStateContext.Provider>
  );
}

/**
 * Narrow subscription: the stable form state only. Prefer this — it is what a
 * consumer that never reads the typed text should use.
 */
export function useComposeFormStateStableContext(): ComposeFormStateStableContextValue {
  const context = useContext(ComposeFormStateContext);
  if (!context) {
    throw new Error("useComposeFormStateStableContext must be used within a ComposeFormStateProvider");
  }
  return context;
}
